import { useState, useEffect } from 'react';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useCopywritingInstructions } from '@/contexts/CopywritingInstructionsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/ui/rich-text-editor';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const GlobalCopywritingInstructionsDialog = () => {
  const { currentBrandId } = useBrandManager();
  const { isOpen, editingInstruction, closeInstructionsDialog } = useCopywritingInstructions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    set: '',
    copywriting_instructions: '',
    copywriting_style: ''
  });

  // Reset form when dialog opens/closes or editingInstruction changes
  useEffect(() => {
    if (isOpen) {
      // When dialog opens, populate form based on editingInstruction
      if (editingInstruction) {
        // Edit mode - populate with existing data
        setFormData({
          set: editingInstruction.set || '',
          copywriting_instructions: editingInstruction.copywriting_instructions || '',
          copywriting_style: editingInstruction.copywriting_style || ''
        });
      } else {
        // Add mode - reset to empty
        setFormData({
          set: '',
          copywriting_instructions: '',
          copywriting_style: ''
        });
      }
    } else {
      // Reset form when dialog closes
      setFormData({
        set: '',
        copywriting_instructions: '',
        copywriting_style: ''
      });
    }
  }, [editingInstruction, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBrandId) return;

    setIsSubmitting(true);
    try {
      const payload = {
        brand_id: currentBrandId,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        ...formData
      };

      if (editingInstruction) {
        const { error } = await supabase
          .from('os_copywriting_instructions')
          .update(payload)
          .eq('id', editingInstruction.id);
        
        if (error) throw error;
        toast.success('Copywriting instructions updated successfully');
      } else {
        const { error } = await supabase
          .from('os_copywriting_instructions')
          .insert(payload);
        
        if (error) throw error;
        toast.success('Copywriting instructions created successfully');
      }

      setFormData({ set: '', copywriting_instructions: '', copywriting_style: '' });
      closeInstructionsDialog();
      
      // Trigger a custom event to refresh the list
      window.dispatchEvent(new CustomEvent('copywriting-instructions-updated'));
    } catch (error) {
      console.error('Error saving instructions:', error);
      toast.error('Failed to save copywriting instructions');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeInstructionsDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName="Copywriting Instructions Form"
            popupContext="/copywriting-instructions"
            isOpen={isOpen}
          >
            {editingInstruction ? 'Edit' : 'Create'} Copywriting Instructions
          </DialogTitleWithBug>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="space-y-4 overflow-y-auto flex-1 pr-2 pb-4">
            <div>
              <Label htmlFor="set">Instruction Set Name</Label>
              <Input
                id="set"
                value={formData.set}
                onChange={(e) => setFormData({ ...formData, set: e.target.value })}
                placeholder="e.g., Professional Blog Posts, Social Media, Email Marketing"
                required
                className="pl-4"
              />
            </div>
            
            <div>
              <Label htmlFor="instructions">Copywriting Instructions</Label>
              <RichTextEditor
                key={`instructions-${editingInstruction?.id || 'new'}-${isOpen}`}
                value={formData.copywriting_instructions || ''}
                onChange={(value) => setFormData({ ...formData, copywriting_instructions: value })}
                placeholder="Detailed instructions for copywriting approach, structure, key points to include..."
                className="min-h-[200px]"
              />
            </div>
            
            <div>
              <Label htmlFor="style">Copywriting Style</Label>
              <RichTextEditor
                key={`style-${editingInstruction?.id || 'new'}-${isOpen}`}
                value={formData.copywriting_style || ''}
                onChange={(value) => setFormData({ ...formData, copywriting_style: value })}
                placeholder="Tone of voice, style guidelines, brand personality to reflect..."
                className="min-h-[150px]"
              />
            </div>
          </div>
          
          <div className="flex gap-2 pt-4 border-t flex-shrink-0">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingInstruction ? 'Update' : 'Create')}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={closeInstructionsDialog}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

